import React, { useMemo, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js'
import { useSelector } from 'react-redux'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

const BAR_FRACTION = 0.3

// קפיצת ציר "נעימה" לפי סדר הגודל של הסה"כ
const niceStep = (max) => {
    if (max <= 300) return 25
    if (max <= 800) return 50
    if (max <= 1500) return 100
    if (max <= 3000) return 200
    if (max <= 6000) return 500
    return 1000
}

// פאנל צד לעמוד "מי נמדד" — מוצג בלחיצה על כרטיס "תלמידים בתכנית המדידה":
// עמודה יחידה, תכלת (למטה) = ביצעו בפועל, לבן (למעלה) = יתרת התלמידים בתכנית.
// התוויות הן תוספת מעל הקנבס — כדי שהמספר יהיה מודגש (700) ושאר
// המשפט רגיל (400), כי קנבס לא מאפשר בתווית אחת
const MeasurementTotalsPanel = ({ total = 0, executed = 0 }) => {
    const { brokenDisplay } = useSelector(state => state.general)
    // מלבן אזור הציור של הגרף (בפיקסלים יחסית לקנבס) — נלכד אחרי כל layout,
    // ומשמש למיקום תוויות ה-HTML לצד העמודה
    const [area, setArea] = useState(null)

    const { max, step } = useMemo(() => {
        const step = niceStep(total || 1)
        let max = Math.ceil((total || 1) / step) * step
        if (max - total < step * 0.15) max += step
        return { max, step }
    }, [total])

    const areaPlugin = useMemo(() => ({
        id: 'captureChartArea',
        afterLayout: (chart) => {
            const a = chart.chartArea
            if (!a) return
            setArea(prev =>
                prev && prev.left === a.left && prev.right === a.right && prev.top === a.top && prev.bottom === a.bottom
                    ? prev
                    : { left: a.left, right: a.right, top: a.top, bottom: a.bottom })
        }
    }), [])

    // עיצוב העמודה
    const barGeometry = {
        borderRadius: 10,
        borderSkipped: false,
        barPercentage: BAR_FRACTION,
        categoryPercentage: 1,
        grouped: false,
    }

    const data = useMemo(() => ({
        labels: [''],
        datasets: [
            {
                label: 'תלמידים ביצעו בפועל',
                data: [executed],
                backgroundColor: '#85B6FF',
                // מבטל את ההתנהגות האוטומטית של הגרף על הובר — הצבע נשאר קבוע
                hoverBackgroundColor: '#85B6FF',
                ...barGeometry,
            },
            {
                label: 'תלמידים בתכנית המדידה',
                data: [total],
                backgroundColor: '#FFFFFF',
                hoverBackgroundColor: '#FFFFFF',
                ...barGeometry,
            },
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [executed, total])

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            // הפלאגין נרשם גלובלית בגרפים אחרים — מוודאים שלא יצייר כאן כלום
            datalabels: { display: false },
        },
        scales: {
            x: { grid: { display: false }, ticks: { display: false } },
            y: {
                beginAtZero: true,
                max,
                ticks: {
                    stepSize: step,
                    callback: (v) => `${v.toLocaleString()} תלמידים`,
                    font: { size: 11.5 },
                },
                grid: { color: '#E2E8F0' },
            },
        },
    }), [max, step])

    // מיקומי התוויות: מימין לעמודה, ממורכזות אנכית במרכז כל מקטע גלוי
    const labels = useMemo(() => {
        if (!area || !max) return null
        const plotHeight = area.bottom - area.top
        const yFor = (value) => area.bottom - (value / max) * plotHeight
        const barWidth = (area.right - area.left) * BAR_FRACTION
        const labelLeft = (area.left + area.right) / 2 + barWidth / 2 + 14

        return {
            left: labelLeft,
            blueCenterY: yFor(executed / 2),
            whiteCenterY: yFor(executed + Math.max(total - executed, 0) / 2),
        }
    }, [area, max, executed, total])

    if (!total)
        return <div className='totals-empty'>{'אין נתונים עדיין'}</div>

    return (
        <div className='totals-chart'>
            <div className='totals-chart-inner'>
                <Bar data={data} options={options} plugins={[areaPlugin]} />
                {labels &&
                    <>
                        <div className='totals-bar-label' style={{ left: labels.left, top: labels.blueCenterY }}>
                            <b className={`${brokenDisplay && 'blurred2'}`}>{executed.toLocaleString()}</b>
                            <div>{'תלמידים'}</div>
                            <div>{'ביצעו בפועל'}</div>
                        </div>
                        <div className='totals-bar-label' style={{ left: labels.left, top: labels.whiteCenterY }}>
                            <b className={`${brokenDisplay && 'blurred2'}`}>{total.toLocaleString()}</b>
                            <div>{'תלמידים'}</div>
                            <div>{'בתכנית'}</div>
                            <div>{'המדידה'}</div>
                        </div>
                    </>}
            </div>
        </div>
    )
}

export default MeasurementTotalsPanel
